import {CustomFileData} from '@/shared/utils'
import {createContext, FC, MutableRefObject, ReactNode, useContext, useMemo, useRef} from 'react'
import {OWN_ROOT, StorageRoot} from '../core/storage'

/**
 * What Copy or Cut put aside for the next Paste. At most one of the two holds files; a paste
 * empties both.
 *
 * Refs, not state: nothing renders from the clipboard while it changes. The context menu reads it
 * when it opens, to decide whether to offer Paste.
 */
export interface FilesClipboard {
  /** Paste copies these. */
  filesToCopy: MutableRefObject<CustomFileData[]>
  /** Paste moves these. */
  filesToMove: MutableRefObject<CustomFileData[]>
  /**
   * The root the files were copied or cut in: their ids are relative to it. A paste into another
   * root (My Files and a shared folder) is a transfer between two people's spaces (E8).
   */
  sourceRoot: MutableRefObject<StorageRoot>
}

const FilesClipboardContext = createContext<FilesClipboard | null>(null)

/**
 * One clipboard for a Files browser, shared by everything under it. The keyboard shortcuts and the
 * context menu each run their own `useFileTable`, so without this they kept separate clipboards: a
 * Ctrl+C could not be pasted from the menu, nor a menu Copy with Ctrl+V.
 */
export const FilesClipboardProvider: FC<{children: ReactNode}> = ({children}) => {
  const filesToCopy = useRef<CustomFileData[]>([])
  const filesToMove = useRef<CustomFileData[]>([])
  const sourceRoot = useRef<StorageRoot>(OWN_ROOT)
  const clipboard = useMemo(() => ({filesToCopy, filesToMove, sourceRoot}), [])
  return (
    <FilesClipboardContext.Provider value={clipboard}>{children}</FilesClipboardContext.Provider>
  )
}

/**
 * The enclosing browser's clipboard. Outside a {@link FilesClipboardProvider} — a hook rendered on
 * its own, as in unit tests — the caller gets a clipboard of its own.
 */
export function useFilesClipboard(): FilesClipboard {
  const shared = useContext(FilesClipboardContext)
  const filesToCopy = useRef<CustomFileData[]>([])
  const filesToMove = useRef<CustomFileData[]>([])
  const sourceRoot = useRef<StorageRoot>(OWN_ROOT)
  return shared ?? {filesToCopy, filesToMove, sourceRoot}
}
